/**
 * Search History API functions
 */

import axiosInstance from '../utils/axiosInstance';
import { SEARCH_HISTORY_URL } from '../constants/travel';
import { createLogger } from '../utils/logger';

const log = createLogger('searchHistoryApi');

/**
 * Get user's search history (paginated)
 */
export const getSearchHistory = async (limit: number = 20, skip: number = 0) => {
  try {
    const response = await axiosInstance.get(SEARCH_HISTORY_URL, {
      params: { limit, skip }
    });
    return response.data;
  } catch (error: any) {
    log.error('Error getting search history', error);
    throw new Error(`Failed to get search history: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Get specific search by ID with full state restoration
 */
export const getSearchById = async (searchId: string, includeFullState: boolean = true) => {
  try {
    const response = await axiosInstance.get(`${SEARCH_HISTORY_URL}/${searchId}`, {
      params: { include_full_state: includeFullState }
    });
    return response.data;
  } catch (error: any) {
    log.error(`Error getting search ${searchId}`, error);
    throw new Error(`Failed to get search: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Update search metadata (title, notes, favorite, tags)
 */
export const updateSearchMetadata = async (
  searchId: string,
  metadata: {
    user_title?: string;
    user_notes?: string;
    is_favorite?: boolean;
    tags?: string[];
  }
) => {
  try {
    const response = await axiosInstance.patch(`${SEARCH_HISTORY_URL}/${searchId}`, metadata);
    return response.data;
  } catch (error: any) {
    log.error(`Error updating search ${searchId}`, error);
    throw new Error(`Failed to update search: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Delete a search (soft delete)
 */
export const deleteSearch = async (searchId: string) => {
  try {
    const response = await axiosInstance.delete(`${SEARCH_HISTORY_URL}/${searchId}`);
    return response.data;
  } catch (error: any) {
    log.error(`Error deleting search ${searchId}`, error);
    throw new Error(`Failed to delete search: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Restore a search - returns full travel state
 */
export const restoreSearch = async (searchId: string) => {
  try {
    const response = await axiosInstance.post(`${SEARCH_HISTORY_URL}/${searchId}/restore`);
    return response.data;
  } catch (error: any) {
    log.error(`Error restoring search ${searchId}`, error);
    throw new Error(`Failed to restore search: ${error.response?.data?.detail || error.message}`);
  }
};
